import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { fromEvent } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { FormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CommonModule, DOCUMENT } from '@angular/common';
import { TableSearchService } from '../../../services/search/table-search.service';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { SharedModule } from '../shared.module';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';

@UntilDestroy()
@Component({
    selector: 'lf-material-search',
    templateUrl: './material-search.component.html',
    styleUrls: ['./material-search.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        SharedModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule
    ],
})
export class MaterialSearchComponent implements OnInit, OnChanges, AfterViewInit
{
    @Input() isLoading: boolean;
    @Input() defaultValue: string = '';
    @Input() defaultPlaceholder: string;
    @Input() delayTime = 250;
    @Input() hasGroupAddon = true;
    @Input() hasButtonStyles = false;

    // adding here as we clean up mat-input styles
    @Input() controlName = 'search';
    @Input() newStyles = false;
    newInput = false;

    @Output() updated: EventEmitter<string> = new EventEmitter<string>();

    @ViewChild('searchInput') searchInput: ElementRef;

    form: UntypedFormGroup;

    constructor(
            private _fb: UntypedFormBuilder,
            @Inject(DOCUMENT) private document: Document,
            private _tableSearchService: TableSearchService
    ) {
    }

    get control() {
        return this.form.get(this.controlName) as UntypedFormControl;
    }

    get searchValue() {
        return this.control.value;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.hasOwnProperty('newStyles') && changes.newStyles && changes.newStyles.isFirstChange()) {
            this.newInput = this.newStyles;
        }
    }

    ngOnInit() {
        this.defaultPlaceholder = this.defaultPlaceholder ?? 'Search...';
        if (!this.form) {
            this.form = this._fb.group({
                [this.controlName]: [this.defaultValue],
            });
        }

        if (this.newInput) {
            this.control.valueChanges.pipe(debounceTime(this.delayTime), distinctUntilChanged(), untilDestroyed(this)).subscribe({
                next: () => this.emitChange()
            });
        }
    }

    ngAfterViewInit() {
        if (!this.newInput) {
            fromEvent(this.searchInput.nativeElement, 'keyup')
                    .pipe(untilDestroyed(this))
                    .pipe(
                            debounceTime(this.delayTime),
                            distinctUntilChanged(),
                            tap(() => this.emitChange()),
                    )
                    .subscribe();

            fromEvent(this.searchInput.nativeElement, 'paste')
                    .pipe(untilDestroyed(this))
                    .pipe(
                            debounceTime(this.delayTime),
                            distinctUntilChanged(),
                            tap(() => this.emitChange()),
                    )
                    .subscribe();
        }
    }

    clearSearch() {
        this.control.setValue('');

        if(!this.newInput) {
            this.emitChange();
        }
    }

    focus() {
        this.searchInput.nativeElement.focus();
    }

    hasFocus(): boolean {
        return this.searchInput ? this.searchInput.nativeElement === this.document.activeElement : false;
    }

    private emitChange() {
        this.updated.emit(this.control.value);
        this._tableSearchService.searchUpdated(this.control.value); // <-- Notify sibling to reset paginator
    }
}
